import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/lib/AuthContext";

/** Render `children` only when the user holds at least one of `codes`.
 *
 * Otherwise render `fallback` (default: 403 card). Use this around sections
 * that the backend already gates with `require_permission(...)`: the API will
 * reject the call anyway, but the UI shouldn't tease an action the user can't
 * perform.
 */
export function RequirePermission({
  codes,
  children,
  fallback,
}: {
  codes: string | string[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const list = Array.isArray(codes) ? codes : [codes];
  const allowed = !!user?.permissions?.some((c) => list.includes(c));
  if (allowed) return <>{children}</>;
  if (fallback !== undefined) return <>{fallback}</>;
  return (
    <div className="card p-10 text-center">
      <p className="font-mono text-xs uppercase tracking-widest text-danger">
        forbidden
      </p>
      <p className="text-text-primary mt-2">{t("common.forbidden")}</p>
      <p className="text-text-muted text-sm mt-2">
        {t("common.requiresOneOf")} <span className="font-mono">{list.join(", ")}</span>
      </p>
    </div>
  );
}
